import React, { useState } from 'react';
import { Box, Typography, Button, TextField } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import { COLORS } from '../styles';

function DownloadReportScreen({ driver, downloadCounts, onProceed, onSaveNote }) {
  const flights = driver?.flights || [];
  const rows = flights.map(f => ({
    flight: f.flightNumber,
    destination: f.destination,
    expected: f.bags || 0,
    got: downloadCounts[f.uniqueId] || 0,
  }));
  const totalExpected = rows.reduce((t, r) => t + r.expected, 0);
  const totalGot = rows.reduce((t, r) => t + r.got, 0);
  const allMatched = totalGot === totalExpected;
  const [note, setNote] = useState('');

  const handleProceed = () => {
    const trimmed = note.trim();
    if (trimmed) onSaveNote?.(trimmed);
    onProceed();
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 1.5, pb: 2, overflow: 'hidden' }}>
      {/* Headline */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        {allMatched ? (
          <CheckCircleIcon sx={{ color: COLORS.success, fontSize: 28 }} />
        ) : (
          <WarningAmberIcon sx={{ color: COLORS.warn, fontSize: 28 }} />
        )}
        <Box>
          <Typography sx={{ fontSize: '0.6rem', color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: 0.6 }}>
            Download Report
          </Typography>
          <Typography sx={{ fontSize: '0.95rem', color: COLORS.textPrimary, fontWeight: 700, lineHeight: 1.1 }}>
            {totalGot} of {totalExpected} bags
          </Typography>
        </Box>
      </Box>

      {/* Rows — natural height so notes/button sit right below them
          without leaving a giant gap. */}
      <Box>
        {rows.map((r, i) => {
          const matched = r.got === r.expected;
          return (
            <Box key={i} sx={{
              display: 'flex',
              alignItems: 'center',
              py: 0.55,
              px: 0.75,
              borderBottom: `1px solid ${COLORS.divider}`,
              backgroundColor: matched ? 'transparent' : 'rgba(251, 191, 36, 0.08)',
            }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.78rem', color: COLORS.textPrimary, fontWeight: 600, lineHeight: 1.1 }}>
                  {r.flight} <Box component="span" sx={{ color: COLORS.textMuted, fontWeight: 400 }}>→ {r.destination}</Box>
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'right', minWidth: 60 }}>
                <Typography sx={{
                  fontSize: '0.82rem',
                  color: matched ? COLORS.success : COLORS.warn,
                  fontWeight: 700,
                  lineHeight: 1,
                }}>
                  {r.got} / {r.expected}
                </Typography>
                {!matched && (
                  <Typography sx={{ fontSize: '0.5rem', color: COLORS.warn, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                    Short {r.expected - r.got}
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* Optional note for dispatch — single-row to keep layout tight */}
      <Box sx={{ mt: 0.75 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.3 }}>
          <ChatBubbleOutlineIcon sx={{ color: COLORS.accent, fontSize: 12 }} />
          <Typography sx={{
            fontSize: '0.55rem',
            color: COLORS.accent,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: 0.6,
          }}>
            Note for Dispatch (optional)
          </Typography>
        </Box>
        <TextField
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={allMatched ? 'Anything to flag?' : 'Why short? Damaged bag, late tag, etc.'}
          multiline
          minRows={1}
          maxRows={2}
          fullWidth
          size="small"
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: COLORS.surface,
              color: COLORS.textPrimary,
              fontSize: '0.72rem',
              padding: '5px 8px',
              '& fieldset': { borderColor: COLORS.divider },
              '&:hover fieldset': { borderColor: COLORS.accentDim },
              '&.Mui-focused fieldset': { borderColor: COLORS.accent },
            },
            '& .MuiInputBase-inputMultiline': { padding: 0 },
          }}
        />
      </Box>

      <Button
        fullWidth
        onClick={handleProceed}
        sx={{
          backgroundColor: COLORS.accent,
          color: '#0a1020',
          fontWeight: 700,
          py: 1,
          fontSize: '0.85rem',
          mt: 0.75,
          flexShrink: 0,
          '&:hover': { backgroundColor: COLORS.accentDim },
        }}
      >
        START DRIVE ▸
      </Button>
    </Box>
  );
}

export default DownloadReportScreen;
